import type { ReactNode } from "react";

/*
 * Lucide-Symbole des Lesepfads als Inline-Linien (R-F: Lucide, kein Sprite; das Bausteinpaket führt keine Symbol-Bibliothek
 * als Abhängigkeit, wie `AnsichtWahl` und `symbole.tsx`). Pfade 1:1 aus lucide (info, circle-check, triangle-alert,
 * octagon-alert, file-text, server, calendar, image, workflow, chevron-right, chevron-down). Reine Zierde (`aria-hidden`):
 * die Bedeutung steht immer im Text daneben.
 */
function Lucide({
  children,
  gross = false,
  klein = false,
  winzig = false,
}: {
  children: ReactNode;
  gross?: boolean;
  klein?: boolean;
  winzig?: boolean;
}) {
  return (
    <>
      {/* Preline: component/alerts/alerts-soft-color-variants */}
      <svg
        className={gross ? "shrink-0 size-5" : klein ? "shrink-0 size-3.5" : winzig ? "shrink-0 size-3" : "shrink-0 size-4"}
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        {children}
      </svg>
    </>
  );
}

export const SymInfo = () => (
  <Lucide gross>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </Lucide>
);
export const SymTipp = () => (
  <Lucide gross>
    <circle cx="12" cy="12" r="10" />
    <path d="m9 12 2 2 4-4" />
  </Lucide>
);
export const SymWarnung = () => (
  <Lucide gross>
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </Lucide>
);
export const SymWichtig = () => (
  <Lucide gross>
    <path d="M12 16h.01" />
    <path d="M12 8v4" />
    <path d="M15.312 2a2 2 0 0 1 1.414.586l4.688 4.688A2 2 0 0 1 22 8.688v6.624a2 2 0 0 1-.586 1.414l-4.688 4.688a2 2 0 0 1-1.414.586H8.688a2 2 0 0 1-1.414-.586l-4.688-4.688A2 2 0 0 1 2 15.312V8.688a2 2 0 0 1 .586-1.414l4.688-4.688A2 2 0 0 1 8.688 2z" />
  </Lucide>
);
/** Die Symbole der Verweis-Arten nehmen `klein` (size-3.5, Segment des Chips); ohne `klein` size-4. */
export interface SymGroesse {
  klein?: boolean;
}
export const SymSeite = ({ klein = false }: SymGroesse) => (
  <Lucide klein={klein}>
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    <path d="M10 9H8" />
    <path d="M16 13H8" />
    <path d="M16 17H8" />
  </Lucide>
);
export const SymServer = ({ klein = false }: SymGroesse) => (
  <Lucide klein={klein}>
    <rect width="20" height="8" x="2" y="2" rx="2" ry="2" />
    <rect width="20" height="8" x="2" y="14" rx="2" ry="2" />
    <path d="M6 6h.01" />
    <path d="M6 18h.01" />
  </Lucide>
);
export const SymKalender = ({ klein = false }: SymGroesse) => (
  <Lucide klein={klein}>
    <path d="M8 2v4" />
    <path d="M16 2v4" />
    <rect width="18" height="18" x="3" y="4" rx="2" />
    <path d="M3 10h18" />
  </Lucide>
);
export const SymPerson = ({ klein = false }: SymGroesse) => (
  <Lucide klein={klein}>
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </Lucide>
);
export const SymPersonAus = ({ klein = false }: SymGroesse) => (
  <Lucide klein={klein}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <line x1="17" x2="22" y1="8" y2="13" />
    <line x1="22" x2="17" y1="8" y2="13" />
  </Lucide>
);
export const SymVerboten = ({ klein = false }: SymGroesse) => (
  <Lucide klein={klein}>
    <circle cx="12" cy="12" r="10" />
    <path d="m4.9 4.9 14.2 14.2" />
  </Lucide>
);
export const SymZeitung = ({ klein = false }: SymGroesse) => (
  <Lucide klein={klein}>
    <path d="M15 18h-5" />
    <path d="M18 14h-8" />
    <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-4 0v-9a2 2 0 0 1 2-2h2" />
    <rect width="8" height="4" x="10" y="6" rx="1" />
  </Lucide>
);
export const SymBild = () => (
  <Lucide gross>
    <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
    <circle cx="9" cy="9" r="2" />
    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
  </Lucide>
);
export const SymAblauf = ({ klein = false }: SymGroesse) => (
  <Lucide klein={klein}>
    <rect width="8" height="8" x="3" y="3" rx="2" />
    <path d="M7 11v4a2 2 0 0 0 2 2h4" />
    <rect width="8" height="8" x="13" y="13" rx="2" />
  </Lucide>
);
/** Lucide `square-check`: Symbol der Verweis-Art „Aufgabe“ (Modulwort „Projekte“). */
export const SymAufgabe = ({ klein = false }: SymGroesse) => (
  <Lucide klein={klein}>
    <rect width="18" height="18" x="3" y="3" rx="2" />
    <path d="m9 12 2 2 4-4" />
  </Lucide>
);
/** Lucide `info` in der kleinsten Größe (size-3): der Hinweis hinter dem Namen im Chip der Grundsicht (W191 A), kein Verbot. */
export const SymInfoWinzig = () => (
  <Lucide winzig>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </Lucide>
);
/** Dasselbe Symbol in `size-3.5` für die Fußzeile der Hover-Karte. */
export const SymInfoKlein = () => (
  <Lucide klein>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </Lucide>
);
export const SymRechts = () => (
  <Lucide>
    <path d="m9 18 6-6-6-6" />
  </Lucide>
);
export const SymUnten = () => (
  <Lucide>
    <path d="m6 9 6 6 6-6" />
  </Lucide>
);
